import React, { useState, useEffect } from 'react';
import BaseModal from '../../../../components/modals/BaseModal';
import { updateInventoryItem } from '../../../../repository/InventoryDAO';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem, InputAdornment, useMediaQuery } from '@mui/material';
import { useToast } from '../../../../context/toast/ToastContext';
import AddSupplierModal from '../../Suppliers/modals/AddSupplierModal';
import AddLocationModal from '../../Locations/modals/AddLocationModal';
import AddUOMModal from '../../UnitOfMeasure/modals/AddUOMModal';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { IconPlus } from '@tabler/icons-react';
import { useInventoryContext } from '../../../../context/inventory_context/InventoryContext';
import { Modules } from '../../../../utils/permissions/Modules';
import usePermissions from '../../../../utils/permissions/UsePermissions';
import { PermissionTypes } from '../../../../utils/permissions/PermissionTypes';

const EditInventoryModal = ({ show, onHide, inventoryItem }) => {
    const [productPrice, setProductPrice] = useState("");
    const [minimumQuantity, setMinimumQuantity] = useState(0);
    const [reorderQuantity, setReorderQuantity] = useState(0);
    const [productSupplier, setProductSupplier] = useState("");
    const [productLocation, setProductLocation] = useState("");
    const [productUOM, setProductUOM] = useState("");
    const [productSku, setProductSku] = useState("")
    const [link, setLink] = useState("");
    const [productPriceError, setProductPriceError] = useState(false);
    const [minimumQuantityError, setMinimumQuantityError] = useState(false);
    const [reorderQuantityError, setReorderQuantityError] = useState(false);
    const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
    const [showAddLocationModal, setShowAddLocationModal] = useState(false);
    const [showAddUOMModal, setShowAddUOMModal] = useState(false);
    const { setLoadingData } = useLoadingContext();
    const { firebaseUser } = useUserContext();
    const { UOMs, suppliers, inventoryLocations } = useInventoryContext();
    const { showToast } = useToast();
    const { t } = useTranslation();
    const { hasPermission } = usePermissions();
    // Detect if the screen size is mobile
    const isMobile = useMediaQuery('(max-width:600px)');

    useEffect(() => {
        if (inventoryItem) {
            setProductSku(inventoryItem.sku);
            setProductPrice(inventoryItem.price);
            setMinimumQuantity(inventoryItem.minimum_quantity);
            setReorderQuantity(inventoryItem.reorder_quantity);
            setProductSupplier(inventoryItem.supplier);
            setProductLocation(inventoryItem.location);
            setProductUOM(inventoryItem.unit_of_measure);
            setLink(inventoryItem.link);
        }
    }, [inventoryItem, show]);

    const handleSubmit = async () => {
        let hasError = false;
        if (productPrice === "" || isNaN(productPrice) || parseFloat(productPrice) < 0) {
            setProductPriceError(true);
            hasError = true;
        }
        if (minimumQuantity === "" || isNaN(minimumQuantity)) {
            setMinimumQuantityError(true);
            hasError = true;
        }
        if (reorderQuantity === "" || isNaN(reorderQuantity)) {
            setReorderQuantityError(true);
            hasError = true;
        }
        if (hasError) return;

        setLoadingData(true);
        const updatedItem = {
            price: parseFloat(productPrice),
            minimum_quantity: parseFloat(minimumQuantity),
            reorder_quantity: parseFloat(reorderQuantity),
            supplier: productSupplier,
            location: productLocation,
            unit_of_measure: productUOM,
            link: link
        };
        await updateInventoryItem(firebaseUser.organization, inventoryItem.id, updatedItem, showToast);
        setLoadingData(false);
        onHide();
    };

    const resetForm = () => {
        setProductSku("")
        setProductPrice("");
        setMinimumQuantity(0);
        setReorderQuantity(0);
        setProductSupplier("");
        setProductUOM("");
        setProductLocation("");
        setLink("");
        setProductPriceError(false);
        setMinimumQuantityError(false);
        setReorderQuantityError(false);
    };

    const actions = (
        <Button onClick={handleSubmit}>{t(StringKeys.SUBMIT)}</Button>
    );

    return (
        <BaseModal
            open={show}
            onClose={() => {
                resetForm();
                onHide();
            }}
            title={t(StringKeys.EDIT_X, { x: `${inventoryItem.name}` })}
            actions={actions}
        >
            <Grid container spacing={2} direction={isMobile ? 'column' : 'row'}>
                <Grid item xs={isMobile ? 12 : 4}>
                    <TextField
                        margin="dense"
                        id="price"
                        label={t(StringKeys.PRICE)}
                        type="number"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputProps: { 
                                min: "0", 
                                step: "0.01",
                                pattern: "^\d*(\.\d{0,2})?$",  // Only allows up to 2 decimal places
                                onKeyDown: (e) => {
                                    if (["e", "E", "+", "-"].includes(e.key)) {
                                        e.preventDefault();
                                    }
                                }
                            }
                        }}
                        fullWidth
                        variant="outlined"
                        value={productPrice}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || (!isNaN(value) && parseFloat(value) >= 0)) {
                                setProductPrice(value);
                                if (productPriceError) setProductPriceError(false);
                            }
                        }}
                        error={productPriceError}
                        helperText={productPriceError ? t(StringKeys.PRICE_IS_REQUIRED) : ""}
                        required
                    />
                </Grid>
                <Grid item xs={isMobile ? 12 : 4}>
                    <TextField
                        margin="dense"
                        id="minimum-quantity"
                        label={t(StringKeys.MINIMUM_QUANTITY)}
                        type="number"
                        InputProps={{ 
                            inputProps: { 
                                min: "0", 
                                step: "1",
                                onKeyDown: (e) => {
                                    if (["e", "E", "+", "-"].includes(e.key)) {
                                        e.preventDefault();
                                    }
                                }
                            } 
                        }}
                        fullWidth
                        variant="outlined"
                        value={minimumQuantity}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || (!isNaN(value) && parseInt(value) >= 0)) {
                                setMinimumQuantity(value);
                                if (minimumQuantityError) setMinimumQuantityError(false);
                            }
                        }}
                        error={minimumQuantityError}
                        helperText={minimumQuantityError ? t(StringKeys.MINIMUM_QUANTITY_IS_REQUIRED) : ""}
                        required
                    />
                </Grid>
                <Grid item xs={isMobile ? 12 : 4}>
                    <TextField
                        margin="dense"
                        id="reorder-quantity"
                        label={t(StringKeys.REORDER_QUANTITY)}
                        type="number"
                        InputProps={{ 
                            inputProps: { 
                                min: "0", 
                                step: "1",
                                onKeyDown: (e) => {
                                    if (["e", "E", "+", "-"].includes(e.key)) {
                                        e.preventDefault();
                                    }
                                }
                            } 
                        }}
                        fullWidth
                        variant="outlined"
                        value={reorderQuantity}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || (!isNaN(value) && parseInt(value) >= 0)) {
                                setReorderQuantity(value);
                                if (reorderQuantityError) setReorderQuantityError(false);
                            }
                        }}
                        error={reorderQuantityError}
                        helperText={reorderQuantityError ? t(StringKeys.REORDER_QUANTITY_IS_REQUIRED) : ""}
                        required
                    />
                </Grid>
            </Grid>
    
            <Grid container spacing={2} direction={isMobile ? 'column' : 'row'}>
                <Grid item xs={isMobile ? 12 : 6}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="product-uom-label">{t(StringKeys.UNIT_OF_MEASURE)}</InputLabel>
                        <Select
                            labelId="product-uom-label"
                            id="product-uom-select"
                            value={productUOM}
                            label={t(StringKeys.UNIT_OF_MEASURE)}
                            onChange={(e) => {
                                if (e.target.value === 'addUOM') {
                                    setShowAddUOMModal(true);
                                } else {
                                    setProductUOM(e.target.value);
                                }
                            }}
                        >
                            {UOMs.map((uom) => (
                                <MenuItem key={uom.id} value={uom.id}>{uom.id}</MenuItem>
                            ))}
                            {hasPermission(Modules.UNIT_OF_MEASURE, PermissionTypes.WRITE) && (
                                <MenuItem key="addUOM" value="addUOM"><IconPlus /> {t(StringKeys.ADD_UOM)}</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={isMobile ? 12 : 6}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="product-supplier-label">{t(StringKeys.SUPPLIER)}</InputLabel>
                        <Select
                            labelId="product-supplier-label"
                            id="product-supplier-select"
                            value={productSupplier}
                            label={t(StringKeys.SUPPLIER)}
                            onChange={(e) => {
                                if (e.target.value === 'addSupplier') {
                                    setShowAddSupplierModal(true);
                                } else {
                                    setProductSupplier(e.target.value);
                                }
                            }}
                        >
                            {suppliers.map((supplier) => (
                                <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>
                            ))}
                            {hasPermission(Modules.SUPPLIERS, PermissionTypes.WRITE) && (
                                <MenuItem key="addSupplier" value="addSupplier"><IconPlus /> {t(StringKeys.ADD_SUPPLIER)}</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Grid container spacing={2} direction={isMobile ? 'column' : 'row'}>
                <Grid item xs={isMobile ? 12 : 6}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="product-location-label">{t(StringKeys.LOCATION)}</InputLabel>
                        <Select
                            labelId="product-location-label"
                            id="product-location-select"
                            value={productLocation}
                            label={t(StringKeys.LOCATION)}
                            onChange={(e) => {
                                if (e.target.value === 'addLocation') {
                                    setShowAddLocationModal(true);
                                } else {
                                    setProductLocation(e.target.value);
                                }
                            }}
                        >
                            {inventoryLocations.map((location) => (
                                <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>
                            ))}
                            {hasPermission(Modules.LOCATIONS, PermissionTypes.WRITE) && (
                                <MenuItem key="addLocation" value="addLocation"><IconPlus /> {t(StringKeys.ADD_LOCATION)}</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={isMobile ? 12 : 6}>
                    <TextField
                        margin="dense"
                        id="url"
                        label={t(StringKeys.PRODUCT_SKU)}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={productSku}
                        onChange={(e) => setProductSku(e.target.value)}
                    />
                </Grid>
                <Grid item xs={isMobile ? 12 : 6}>
                    <TextField
                        margin="dense"
                        id="url"
                        label={t(StringKeys.URL)}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                    />
                </Grid>
            </Grid>

            <AddSupplierModal
                show={showAddSupplierModal}
                onHide={() => setShowAddSupplierModal(false)}
            />
            <AddLocationModal
                show={showAddLocationModal}
                onHide={() => setShowAddLocationModal(false)}
            />
            <AddUOMModal
                show={showAddUOMModal}
                onHide={() => setShowAddUOMModal(false)}
            />
        </BaseModal>
    );
};

export default EditInventoryModal;
